import { mdiCheck, mdiChevronDown, mdiChevronRight, mdiClose, mdiEyeOffOutline, mdiPencilOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { Tooltip } from '@mui/material';
import _ from 'lodash';
import { observer } from 'mobx-react';
import React from 'react';
import { Row } from 'react-table';
import {
    Amount,
    PayerAdjustmentGroupType,
    PaymentStatusType,
    ProviderChargemasterItem,
    ProviderClaimPayment,
    ProviderClaimPaymentItem,
    ProviderInstitutionalClaimTemplate,
    ProviderNynf3ClaimTemplate,
    ProviderPayer,
    ProviderPayerSubscriber,
    ProviderProfessionalClaimTemplate,
    ProviderProvider,
    SupportChargemasterItem,
    SupportClaimPayment,
    SupportClaimPaymentItem,
    SupportInstitutionalClaimTemplate,
    SupportInstitutionalTemplateServiceLine,
    SupportNynf3ClaimTemplate,
    SupportPayer,
    SupportPayerSubscriber,
    SupportProfessionalClaimTemplate,
    SupportProfessionalTemplateServiceLine,
    SupportProvider,
} from '../../../api';
import { formatNumber, formatDate, normalizeStr } from '../../../services';
import { TooltipTransitionComponent } from '../../base';
import { DataTablePresenter, ITableColumn, renderString, renderStringGroup, Settings } from '../../data-table';
import { getInstitutionalServiceLineKey, getPaymentItemKey } from '../matching-keys';
import { getLatestPaymentsByPayerPriority, getPaymentsWithoutReversals } from '../payment-summary';
import { createProfessionalPaymentItemMatcher } from '../professional-payment-matching';
import { PaymentItems } from '../payment-items/payment-items';
import { formatPayerTitle } from '../claim-overview/claim-overview.utils';
import * as S from './record-of-services.styles';
import * as SectionS from '../claim-section.styles';
import {
    buildMismatchDescription,
    buildMismatchGroupMeta,
    findLikelyMatchLineNumber,
} from './record-of-services.utils';

type PaymentItemWithStatus = (ProviderClaimPaymentItem | SupportClaimPaymentItem) & {
    status?: PaymentStatusType;
    paymentId?: string;
};

interface IRecordOfServicesProps {
    loading: boolean;
    template?:
        | ProviderProfessionalClaimTemplate
        | ProviderInstitutionalClaimTemplate
        | ProviderNynf3ClaimTemplate
        | SupportProfessionalClaimTemplate
        | SupportInstitutionalClaimTemplate
        | SupportNynf3ClaimTemplate;
    providers?: ProviderProvider[] | SupportProvider[];
    chargemasterItems?: ProviderChargemasterItem[] | SupportChargemasterItem[];
    payments?: ProviderClaimPayment[] | SupportClaimPayment[];
    claimPatientPaid?: Amount;
    payerSubscribers?: SupportPayerSubscriber[] | ProviderPayerSubscriber[];
    onEdit?: (line: SupportProfessionalTemplateServiceLine) => void;
    embedded?: boolean;
}

interface IRecord {
    rvu?: string;
    dateOfService?: string;
    procedureCode?: string;
    modifiers?: string[];
    description?: string;
    diagnosis?: string;
    units?: number;
    paid?: number;
    patientPaid?: number;
    patientResponsibility?: number;
    allowed?: number;
    accepted?: number;
    medicare?: number;
    totalCharges?: number;
    patientMaxAmount?: number;
    hasExtendedX12Data?: boolean;
    line?: SupportProfessionalTemplateServiceLine;
    chargemasterItem?: ProviderChargemasterItem | SupportChargemasterItem;
    isSummaryRow?: boolean;
    isMismatchGroupRow?: boolean;
    mismatchCount?: number;
    mismatchGroupContextLabel?: string;
    mismatchGroupReceivedTotal?: number;
    mismatchDescriptionShort?: string;
    isNoLikelyMatch?: boolean;
    items?: PaymentItemWithStatus[];
}

interface IRecordOfServicesState {
    showMismatchedRows: boolean;
}

const MismatchDescriptionCell: React.FC<{
    full?: string;
    short?: string;
    isNoLikelyMatch?: boolean;
}> = ({ full, short, isNoLikelyMatch }) => {
    const [expanded, setExpanded] = React.useState(false);

    if (isNoLikelyMatch || !full) {
        return <S.NoLikelyMatchLabel>No likely match</S.NoLikelyMatchLabel>;
    }

    const isTruncatable = !!short && full !== short;
    const display = expanded || !isTruncatable ? full : short;

    const handleClick = (event: React.MouseEvent): void => {
        event.stopPropagation();

        if (isTruncatable) {
            setExpanded(prev => !prev);
        }
    };

    return (
        <S.MismatchDescriptionTag
            type="button"
            $clickable={isTruncatable}
            onClick={handleClick}
            title={isTruncatable && !expanded ? full : undefined}>
            {display}
            {!expanded && isTruncatable ? '…' : ''}
        </S.MismatchDescriptionTag>
    );
};

const TableHeader = (header: string, align?: 'left' | 'right') => {
    return <S.Header align={align}>{header}</S.Header>;
};

const formatCurrency = formatNumber('Currency');

const isZeroAmount = (value: number | undefined | null): boolean =>
    value === undefined || value === null || value === 0;

const renderCurrencyCell = (value: number | undefined | null): React.ReactElement => {
    const isZero = isZeroAmount(value);

    return <S.StyledCharges $isZero={isZero}>{formatCurrency(isZero ? 0 : value)}</S.StyledCharges>;
};

@observer
export class RecordOfServices extends React.Component<IRecordOfServicesProps, IRecordOfServicesState> {
    private presenter: DataTablePresenter<IRecord>;
    private records: IRecord[] = [];
    private columns: ITableColumn<IRecord>[] = _.compact([
        {
            id: 'count',
            minWidth: 50,
            maxWidth: 50,
            Cell: ({ cell, row }) => {
                if (row.original.isMismatchGroupRow || row.original.isSummaryRow) {
                    return null;
                }

                if (!row.original.line) {
                    return <S.MismatchEyeIcon path={mdiEyeOffOutline} />;
                }

                return <span>{(cell.row.index || 0) + 1}</span>;
            },
        },
        {
            id: 'dateOfService',
            Header: () => TableHeader('Dates of Service', 'left'),
            accessor: 'dateOfService',
        },
        {
            id: 'procedureCode',
            Header: () => TableHeader('CPT/HCPCS', 'left'),
            accessor: 'procedureCode',
            Cell: ({ cell, row }) => {
                if (!cell.value || row.original.isSummaryRow) {
                    return null;
                }

                const procedureCode = cell.value;
                const modifiers = _.compact(row.original.modifiers).join(', ');
                const value = _.compact([procedureCode, modifiers]).join('-');

                return (
                    <Tooltip
                        placement="top"
                        title={<>{row.original.description} </>}
                        open={row.original.description ? undefined : false}
                        TransitionComponent={TooltipTransitionComponent}>
                        <div>
                            <S.StyledTag>{value}</S.StyledTag>{' '}
                            {(row.original.units || 0) > 0 ? `x ${row.original.units}` : null}
                        </div>
                    </Tooltip>
                );
            },
            minWidth: 150,
        },
        {
            id: 'paperwork',
            accessor: (row: IRecord) => row.chargemasterItem?.paperwork,
            Header: () => TableHeader('Paperwork', 'left'),
            Cell: ({ cell, row }) => {
                if (row.original.isSummaryRow) {
                    return null;
                }

                return this.renderChargemasterRequirementCell({
                    required: !!cell.value,
                    claimHasData: this.hasClaimPaperwork(this.props.template, row.original.line),
                    presentDisplay: 'Present',
                    notRequiredTooltip: 'Chargemaster does not require paperwork for this line',
                    presentNotRequiredTooltip:
                        'Paperwork is attached on the claim, but the chargemaster does not require it for this line',
                    requiredMissingTooltip:
                        'Chargemaster requires paperwork and the claim does not satisfy the requirement',
                });
            },
            minWidth: 120,
            show: false,
        },
        {
            id: 'authorization',
            accessor: (row: IRecord) => row.chargemasterItem?.authorization,
            Header: () => TableHeader('Authorization', 'left'),
            Cell: ({ cell, row }) => {
                if (row.original.isSummaryRow) {
                    return null;
                }

                const priorAuthorizationNumber = this.getClaimPriorAuthorizationNumber(
                    this.props.template,
                    row.original.line,
                );

                return this.renderChargemasterRequirementCell({
                    required: !!cell.value,
                    claimHasData: !!priorAuthorizationNumber,
                    presentDisplay: priorAuthorizationNumber ? this.truncateLabel(priorAuthorizationNumber) : 'Present',
                    notRequiredTooltip: 'Chargemaster does not require authorization for this line',
                    presentNotRequiredTooltip: priorAuthorizationNumber
                        ? `Prior authorization ${priorAuthorizationNumber} is on the claim, but the chargemaster does not require it for this line`
                        : 'Prior authorization is on the claim, but the chargemaster does not require it for this line',
                    requiredMissingTooltip:
                        'Chargemaster requires authorization and the claim does not satisfy the requirement',
                });
            },
            minWidth: 150,
            show: false,
        },
        {
            id: 'description',
            accessor: 'description',
            Header: () => TableHeader('Description', 'left'),
            Cell: ({ cell, row }) => {
                if (row.original.isSummaryRow || row.original.isMismatchGroupRow) {
                    return null;
                }

                if (!row.original.line) {
                    return (
                        <MismatchDescriptionCell
                            full={cell.value}
                            short={row.original.mismatchDescriptionShort}
                            isNoLikelyMatch={row.original.isNoLikelyMatch}
                        />
                    );
                }

                return <S.StyledDescription>{cell.value}</S.StyledDescription>;
            },
            minWidth: 200,
        },
        {
            id: 'diagnosis',
            accessor: 'diagnosis',
            Header: () => TableHeader('Diagnosis Codes', 'left'),
            Cell: ({ cell, row }) => {
                if (row.original.isSummaryRow) {
                    return <S.TotalLabel>TOTAL</S.TotalLabel>;
                }
                if (!cell.value) {
                    return <S.StyledDescription>-</S.StyledDescription>;
                }
                const diagnosisCodes = cell.value.split(', ').filter(code => code.trim());
                return (
                    <div>
                        {diagnosisCodes.map((code, index) => (
                            <S.StyledTag key={index}>{code.trim()}</S.StyledTag>
                        ))}
                    </div>
                );
            },
            minWidth: 150,
        },
        {
            id: 'totalCharges',
            accessor: 'totalCharges',
            Header: () => TableHeader('Charges', 'left'),
            Cell: ({ cell }) => renderCurrencyCell(cell.value),
        },
        {
            id: 'accepted',
            accessor: 'accepted',
            Header: () => TableHeader('Accepted', 'left'),
            Cell: ({ cell }) => renderCurrencyCell(cell.value),
        },
        {
            id: 'allowed',
            accessor: 'allowed',
            Header: () => TableHeader('Allowed', 'left'),
            Cell: ({ cell }) => renderCurrencyCell(cell.value),
        },
        {
            id: 'medicare',
            accessor: 'medicare',
            Header: () => TableHeader('Medicare', 'left'),
            Cell: ({ cell }) => renderCurrencyCell(cell.value),
            show: false,
        },
        {
            id: 'paid',
            accessor: 'paid',
            Header: () => TableHeader('Payer Paid', 'left'),
            Cell: ({ cell }) => renderCurrencyCell(cell.value),
        },
        {
            id: 'patientResponsibility',
            accessor: 'patientResponsibility',
            Header: () => TableHeader('Patient Responsibility', 'left'),
            Cell: ({ cell }) => renderCurrencyCell(cell.value),
            minWidth: 150,
        },
        {
            id: 'patientPaid',
            accessor: 'patientPaid',
            Header: () => TableHeader('Patient Paid', 'left'),
            Cell: ({ cell }) => renderCurrencyCell(cell.value),
            minWidth: 120,
        },
        {
            id: 'rvu',
            Header: () => TableHeader('RVU', 'left'),
            accessor: 'chargemasterItem',
            Cell: ({ cell }) => <S.StyledCharges>{cell.value?.rvu}</S.StyledCharges>,
            show: false,
        },
        {
            id: 'patientMaxAmount',
            accessor: 'patientMaxAmount',
            Header: () => TableHeader('Patient Max', 'left'),
            Cell: ({ cell, row }) => {
                if (row.original.isSummaryRow) {
                    return null;
                }
                return renderCurrencyCell(cell.value);
            },
            minWidth: 120,
            show: false,
        },
        this.props.onEdit
            ? {
                  id: 'edit',
                  minWidth: 40,
                  Cell: ({ row }) => {
                      const line = row.original.line;
                      if (row.original.isSummaryRow || !line) {
                          return null;
                      }

                      const handleClick = (e: React.MouseEvent) => {
                          e.stopPropagation();
                          this.props.onEdit?.(line);
                      };

                      return (
                          <S.ActionButton variant="outlined" size="small" type="button" onClick={handleClick}>
                              <Icon path={mdiPencilOutline} size={0.75} />
                          </S.ActionButton>
                      );
                  },
              }
            : undefined,
        {
            id: 'expandable',
            minWidth: 40,
            maxWidth: 40,
            Cell: ({ row }) => {
                if (row.original.isSummaryRow) {
                    return null;
                }

                const path = row.isExpanded ? mdiChevronDown : mdiChevronRight;
                return (
                    <S.ExpandableDiv>
                        <S.ExpandIcon path={path} />
                    </S.ExpandableDiv>
                );
            },
        },
    ]);

    get payments() {
        return getPaymentsWithoutReversals(this.props.payments || []);
    }

    constructor(props: IRecordOfServicesProps) {
        super(props);
        this.state = {
            showMismatchedRows: false,
        };
        this.presenter = new DataTablePresenter({
            name: 'record-services',
            data: [],
            columns: this.columns,
            filterSchemas: [],
        });
    }

    public componentDidMount(): void {
        // NB: load data if possible (e.g., back button)
        this.loadData();
    }

    public componentDidUpdate(): void {
        const { loading } = this.props;
        if (!loading) {
            // NB: load data after loading
            this.loadData();
        }
    }

    public render(): React.ReactElement {
        const settingsCaption = (
            <Settings
                columns={this.presenter.columns}
                hiddenColumnIds={this.presenter.hiddenColumnIds}
                onColumnToggle={this.presenter.onColumnToggle}
                variant="small"
            />
        );

        const table = (
            <S.CardBody>
                <S.StyledDataTable
                    presenter={this.presenter}
                    isLoading={this.props.loading}
                    onRowClick={this.onRowClick}
                    renderRowWrapper={this.renderRowWrapper}
                    renderRowSubComponent={this.renderExpanded}
                    disableSorting={true}
                    hideFiltering={true}
                    hideNavigation={true}
                    hidePagination={true}
                    hideSettings={true}
                />
            </S.CardBody>
        );

        if (this.props.embedded) {
            return (
                <SectionS.ClaimSectionBlock>
                    <SectionS.ClaimSectionHeader>
                        <SectionS.ClaimSectionTitle>Record of Services Provided</SectionS.ClaimSectionTitle>
                        {settingsCaption}
                    </SectionS.ClaimSectionHeader>
                    {table}
                </SectionS.ClaimSectionBlock>
            );
        }

        return (
            <S.StyledTitleCard
                size="large"
                title="Record of Services Provided"
                caption={settingsCaption}>
                {table}
            </S.StyledTitleCard>
        );
    }

    private readonly renderRowWrapper = (row: Row<IRecord>, renderRow: JSX.Element) => {
        if (row.original.isMismatchGroupRow) {
            const receivedTotal = row.original.mismatchGroupReceivedTotal || 0;

            return (
                <S.MismatchGroupRow onClick={this.toggleMismatchedRows}>
                    <S.MismatchGroupLeft>
                        <S.MismatchEyeIcon $headerRow path={mdiEyeOffOutline} />
                        <S.MismatchGroupText>
                            <S.MismatchGroupTitle>
                                {row.original.mismatchCount || 0} payer payment
                                {row.original.mismatchCount === 1 ? '' : 's'} awaiting match
                            </S.MismatchGroupTitle>
                            {row.original.mismatchGroupContextLabel && (
                                <>
                                    <S.MismatchGroupContext>·</S.MismatchGroupContext>
                                    <S.MismatchGroupContext>
                                        {row.original.mismatchGroupContextLabel}
                                    </S.MismatchGroupContext>
                                </>
                            )}
                        </S.MismatchGroupText>
                    </S.MismatchGroupLeft>
                    <S.MismatchGroupRight>
                        {receivedTotal > 0 && (
                            <S.MismatchGroupAmount>
                                {formatNumber('Currency')(receivedTotal)} received
                            </S.MismatchGroupAmount>
                        )}
                        <S.MismatchChevron path={this.state.showMismatchedRows ? mdiChevronDown : mdiChevronRight} />
                    </S.MismatchGroupRight>
                </S.MismatchGroupRow>
            );
        }

        if (row.original.isSummaryRow) {
            return <S.SummaryRow>{renderRow}</S.SummaryRow>;
        }

        if (!row.original.line) {
            return <S.RowWrapper warning>{renderRow}</S.RowWrapper>;
        }

        return <S.RowWrapper warning={false}>{renderRow}</S.RowWrapper>;
    };

    private readonly loadData = (): void => {
        const { template } = this.props;
        if (template) {
            let records: IRecord[] = [];
            if (this.isInstitutionalTemplate(template)) {
                records = this.parseInstitutionalTemplate(template as ProviderInstitutionalClaimTemplate);
            } else if (this.isNynf3Template(template)) {
                records = this.parseNynf3Template(template as ProviderNynf3ClaimTemplate);
            } else if (this.isProfessionalTemplate(template)) {
                records = this.parseProfessionalTemplate(template as ProviderProfessionalClaimTemplate);
            }
            // const sorted = _.reverse(_.sortBy(records, 'dateOfService'));
            if (!_.isEmpty(records) && !_.isEqual(records, this.records)) {
                this.records = records;
                this.presenter.onDataUpdate(this.withSummaryData(records));
                this.presenter.onDataTotalChange(records.length);
            }
        }
    };

    private readonly toggleMismatchedRows = (): void => {
        this.setState(
            prevState => ({
                showMismatchedRows: !prevState.showMismatchedRows,
            }),
            () => {
                this.presenter.onDataUpdate(this.withSummaryData(this.records));
            },
        );
    };

    private readonly withSummaryData = (data: IRecord[]): IRecord[] => {
        const patientResponsibility = _.reduce(
            getLatestPaymentsByPayerPriority(this.payments),
            (acc, el) => {
                acc += this.reformatNumber(_.get(el, 'patientAmount.amount', '0'));
                return acc;
            },
            0,
        );

        const totals = data.reduce<{
            paid: number;
            allowed: number;
            medicare: number;
            accepted: number;
            totalCharges: number;
        }>(
            (acc, el) => {
                if (!el.line) {
                    return acc;
                }

                return {
                    paid: acc.paid + (el.paid || 0),
                    allowed: acc.allowed + (el.allowed || 0),
                    medicare: acc.medicare + (el.medicare || 0),
                    accepted: acc.accepted + (el.accepted || 0),
                    totalCharges: acc.totalCharges + (el.totalCharges || 0),
                };
            },
            {
                paid: 0,
                allowed: 0,
                medicare: 0,
                accepted: 0,
                totalCharges: 0,
            },
        );
        const matchedRecords = data.filter(record => !!record.line);
        const mismatchedRecords = data.filter(record => !record.line);
        const primaryPayer = this.props.payerSubscribers?.[0]?.payer;
        const mismatchGroupMeta = buildMismatchGroupMeta(
            mismatchedRecords,
            this.payments,
            formatPayerTitle(primaryPayer),
            date => formatDate(date, 'MMM d, yyyy'),
        );
        const mismatchGroupRow: IRecord[] = mismatchedRecords.length
            ? [
                  {
                      isMismatchGroupRow: true,
                      mismatchCount: mismatchedRecords.length,
                      mismatchGroupContextLabel: mismatchGroupMeta.contextLabel,
                      mismatchGroupReceivedTotal: mismatchGroupMeta.receivedTotal,
                  },
              ]
            : [];
        const visibleMismatchedRecords = this.state.showMismatchedRows ? mismatchedRecords : [];

        return [
            ...matchedRecords,
            ...mismatchGroupRow,
            ...visibleMismatchedRecords,
            {
                paid: totals.paid,
                patientResponsibility,
                patientPaid: this.reformatNumber(this.props.claimPatientPaid?.amount || '0'),
                allowed: totals.allowed,
                medicare: totals.medicare,
                accepted: totals.accepted,
                totalCharges: totals.totalCharges,
                isSummaryRow: true,
            },
        ];
    };

    private readonly isInstitutionalTemplate = (template: object): boolean => {
        return _.has(template, 'f42ClaimLines');
    };
    private readonly parseInstitutionalTemplate = (template: ProviderInstitutionalClaimTemplate): IRecord[] => {
        const serviceLines = _.get(template, 'f42ClaimLines', []);

        const existingControlNumbers: (string | undefined)[] = _.map(serviceLines, line => {
            return line.x12ControlNumber;
        });

        const paymentItems: PaymentItemWithStatus[] = _.map(this.payments, p => {
            return (p.items || []).map(el => {
                return {
                    ...el,
                    status: p.status,
                    paymentId: p.id,
                } as PaymentItemWithStatus;
            });
        }).flat();

        const latestPayments = getLatestPaymentsByPayerPriority(this.payments || []);

        const itemsMap = new Map<string | undefined, PaymentItemWithStatus[]>();

        paymentItems.forEach(item => {
            const key = getPaymentItemKey(item, existingControlNumbers) || 'NOT_FOUND';
            const items = itemsMap.get(key) || [];
            items.push(item);
            itemsMap.set(key, items);
        });

        const parsedRecords: IRecord[] = _.chain(serviceLines)
            .map(line => {
                const chargemasterItem = _.find([...(this.props.chargemasterItems || [])], {
                    id: line.chargemasterItemId,
                });

                let foundPaymentItems: PaymentItemWithStatus[] = [];

                let lineKey = getInstitutionalServiceLineKey(line);
                let items = itemsMap.get(lineKey);
                if (!items) {
                    lineKey = getInstitutionalServiceLineKey({ ...line, x12ControlNumber: undefined });
                    items = itemsMap.get(lineKey);
                }
                if (items) {
                    foundPaymentItems = [...items];
                    itemsMap.delete(lineKey);
                }

                const paid = foundPaymentItems.reduce((acc, el) => {
                    const amount = this.reformatNumber(_.get(el, 'paidAmount.amount', '0'));
                    return acc + amount;
                }, 0);

                // find the largest amount of unique payment items
                const allowed = Math.max(
                    ...foundPaymentItems.map(el => this.reformatNumber(_.get(el, 'allowedAmount.amount', '0'))),
                );

                const patientResponsibility = foundPaymentItems.reduce((acc, el) => {
                    const adjustments = _.filter(
                        el.adjustments,
                        el => el.group === PayerAdjustmentGroupType.PATIENTRESPONSIBILITY,
                    );
                    const amount = adjustments.reduce((acc2, adj) => {
                        if (el.status === PaymentStatusType.REVERSALOFPREVIOUSPAYMENT) {
                            acc2 -= this.reformatNumber(_.get(adj, 'amount.amount', '0'));
                            return acc2;
                        }

                        const isLatestPayment = latestPayments.some(payment => payment.id === el.paymentId);

                        if (!isLatestPayment) {
                            return acc2;
                        }

                        acc2 += this.reformatNumber(_.get(adj, 'amount.amount', '0'));

                        return acc2;
                    }, 0);

                    return acc + amount;
                }, 0);

                return {
                    controlNumber: line.x12ControlNumber,
                    dateOfService: this.reformatDates(line.f45FromServiceDate, line.f45ThroughServiceDate),
                    procedureCode: line.f44ProcedureCode,
                    modifiers: line.f44ModifierCodes,
                    description: line.x12Narrative,
                    diagnosis: '', // Institutional templates don't have diagnosis pointers in service lines
                    units: line.f46ServiceUnits,
                    paid,
                    allowed: isFinite(allowed) ? allowed : 0,
                    accepted:
                        this.reformatNumber(_.get(chargemasterItem, 'accepted.amount', '0')) *
                        (line.f46ServiceUnits ? line.f46ServiceUnits : 1),
                    medicare: this.reformatNumber(_.get(chargemasterItem, 'medicare.amount', '0')),
                    patientMaxAmount: _.get(chargemasterItem, 'patient.amount')
                        ? this.reformatNumber(_.get(chargemasterItem, 'patient.amount', '0')) *
                          (line.f46ServiceUnits ? line.f46ServiceUnits : 1)
                        : undefined,
                    totalCharges: this.reformatNumber(_.get(line.f47TotalChargeAmount, 'amount', '0')),
                    hasExtendedX12Data: false,
                    chargemasterItem,
                    patientResponsibility,
                    line,
                    items,
                };
            })
            .value();

        const notFoundRecords: IRecord[] = [];

        [...itemsMap.values()].forEach(items => {
            const el = _.first(items);
            if (!el) {
                return;
            }

            const paid = items.reduce((acc, el) => {
                const amount = this.reformatNumber(_.get(el, 'paidAmount.amount', '0'));
                return acc + amount;
            }, 0);

            const allowed = items.reduce((acc, el) => {
                const amount = this.reformatNumber(_.get(el, 'allowedAmount.amount', '0'));
                return acc + amount;
            }, 0);

            const totalCharges = this.reformatNumber(_.get(el, 'chargeAmount.amount', '0'));

            const adjustments = _.filter(
                items.map(item => item.adjustments || []).flat(),
                el => el.group === PayerAdjustmentGroupType.PATIENTRESPONSIBILITY,
            );
            const patientResponsibility = adjustments.reduce((acc2, adj) => {
                acc2 += this.reformatNumber(_.get(adj, 'amount.amount', '0'));

                return acc2;
            }, 0);

            const units = items.reduce((acc, el) => {
                const count = _.get(el, 'paidCount', 0);
                return acc + count;
            }, 0);

            notFoundRecords.push({
                dateOfService: this.reformatDates(el.fromDos, el.throughDos),
                procedureCode: el.procedureCode,
                modifiers: el.modifierCodes,
                ...this.getMismatchDescriptionFields(el),
                diagnosis: '',
                units,
                patientResponsibility,
                paid,
                allowed,
                medicare: undefined,
                accepted: undefined,
                totalCharges,
                hasExtendedX12Data: false,
                line: undefined,
                items,
            });
        });

        return [...parsedRecords, ...notFoundRecords];

        // const serviceLines = _.get(template, 'f42ClaimLines', []);
        // return _.map(serviceLines, line => ({
        //     dateOfService: this.reformatDates(line.f45FromServiceDate, line.f45ThroughServiceDate),
        //     procedureCode: line.f44ProcedureCode,
        //     modifiers: line.f44ModifierCodes,
        //     description: line.f43RevenueCodeDescription,
        //     units: line.f46ServiceUnits,
        //     totalCharges: this.reformatNumber(_.get(line.f47TotalChargeAmount, 'amount', '0')),
        // }));
    };
    private readonly isNynf3Template = (template: object): boolean => {
        return _.has(template, 'f15ServiceLines');
    };
    private readonly parseNynf3Template = (template: ProviderNynf3ClaimTemplate): IRecord[] => {
        const serviceLines = _.get(template, 'f15ServiceLines', []);
        return _.map(serviceLines, line => ({
            dateOfService: this.reformatDates(line.f15aFromDateOfService, line.f15aThroughDateOfService),
            procedureCode: line.f15dProcedureCode,
            modifiers: line.f15dModifierCodes,
            description: line.f15cProcedureCodeDescription,
            diagnosis: '', // Nynf3 templates don't have diagnosis pointers in service lines
            units: this.reformatNumber(line.f15dServiceUnit),
            totalCharges: this.reformatNumber(_.get(line.f15eChargeAmount, 'amount', '0')),
        }));
    };
    private readonly isProfessionalTemplate = (template: object): boolean => {
        return _.has(template, 'f24ServiceLine');
    };

    private readonly truncateLabel = (value: string, maxLength = 12): string => {
        const trimmed = value.trim();

        return trimmed.length > maxLength ? `${trimmed.slice(0, maxLength)}…` : trimmed;
    };

    private readonly getNonEmptyAuthorizationNumber = (value?: string): string | undefined => {
        const trimmed = value?.trim();

        return trimmed || undefined;
    };

    private readonly getClaimPriorAuthorizationNumber = (
        template?: IRecordOfServicesProps['template'],
        line?: SupportProfessionalTemplateServiceLine,
    ): string | undefined => {
        if (!template || !this.isProfessionalTemplate(template)) {
            return undefined;
        }

        const professionalTemplate = template as SupportProfessionalClaimTemplate;

        return (
            this.getNonEmptyAuthorizationNumber(professionalTemplate.f23PrimaryPayerPriorAuthorizationNumber) ||
            this.getNonEmptyAuthorizationNumber(professionalTemplate.f23SecondaryPayerPriorAuthorizationNumber) ||
            this.getNonEmptyAuthorizationNumber(professionalTemplate.f23TertiaryPayerPriorAuthorizationNumber) ||
            this.getNonEmptyAuthorizationNumber(line?.x12PrimaryPayerPriorAuthorizationNumber) ||
            this.getNonEmptyAuthorizationNumber(line?.x12SecondaryPayerPriorAuthorizationNumber) ||
            this.getNonEmptyAuthorizationNumber(line?.x12TertiaryPayerPriorAuthorizationNumber)
        );
    };

    private readonly hasClaimPaperwork = (
        template?: IRecordOfServicesProps['template'],
        line?: SupportProfessionalTemplateServiceLine,
    ): boolean => {
        if (!template || !this.isProfessionalTemplate(template)) {
            return false;
        }

        const professionalTemplate = template as SupportProfessionalClaimTemplate;

        return !_.isEmpty(professionalTemplate.x12Paperwork) || !_.isEmpty(line?.x12Paperwork);
    };

    private readonly renderChargemasterRequirementCell = (options: {
        required: boolean;
        claimHasData: boolean;
        presentDisplay?: string;
        notRequiredTooltip: string;
        presentNotRequiredTooltip: string;
        requiredMissingTooltip: string;
    }): React.ReactElement => {
        const {
            required,
            claimHasData,
            presentDisplay = 'Present',
            notRequiredTooltip,
            presentNotRequiredTooltip,
            requiredMissingTooltip,
        } = options;

        if (!required && !claimHasData) {
            return (
                <Tooltip placement="top" title={notRequiredTooltip} TransitionComponent={TooltipTransitionComponent}>
                    <S.AlignCell>
                        <S.MutedStatusLabel>Not required</S.MutedStatusLabel>
                    </S.AlignCell>
                </Tooltip>
            );
        }

        if (!required && claimHasData) {
            return (
                <Tooltip
                    placement="top"
                    title={presentNotRequiredTooltip}
                    TransitionComponent={TooltipTransitionComponent}>
                    <S.AlignCell>
                        <S.PresentStatusLabel>{presentDisplay}</S.PresentStatusLabel>
                    </S.AlignCell>
                </Tooltip>
            );
        }

        return (
            <S.AlignCell>
                {claimHasData ? (
                    <S.SuccessIcon path={mdiCheck} />
                ) : (
                    <Tooltip
                        placement="top"
                        title={requiredMissingTooltip}
                        TransitionComponent={TooltipTransitionComponent}>
                        <div>
                            <S.WarningIcon path={mdiClose} />
                        </div>
                    </Tooltip>
                )}
            </S.AlignCell>
        );
    };

    private readonly parseProfessionalTemplate = (template: ProviderProfessionalClaimTemplate): IRecord[] => {
        const { f24ServiceLine } = template;
        const existingControlNumbers: (string | undefined)[] = _.map(f24ServiceLine, line => {
            return line.x12ControlNumber;
        });
        const paymentItems: PaymentItemWithStatus[] = _.map(this.payments, p => {
            return (p.items || []).map(el => {
                return {
                    ...el,
                    status: p.status,
                    paymentId: p.id,
                } as PaymentItemWithStatus;
            });
        }).flat();

        const latestPayments = getLatestPaymentsByPayerPriority(this.payments || []);
        const professionalMatcher = createProfessionalPaymentItemMatcher(paymentItems, existingControlNumbers);

        const parsedRecords: IRecord[] = _.chain(f24ServiceLine)
            .map(line => {
                const chargemasterItem = _.find([...(this.props.chargemasterItems || [])], {
                    id: line.chargemasterItemId,
                });

                const foundPaymentItems = professionalMatcher.matchLine(line);

                const paid = foundPaymentItems.reduce((acc, el) => {
                    const amount = this.reformatNumber(_.get(el, 'paidAmount.amount', '0'));
                    return acc + amount;
                }, 0);

                // find the largest amount of unique payment items
                const allowed = Math.max(
                    ...foundPaymentItems.map(el => this.reformatNumber(_.get(el, 'allowedAmount.amount', '0'))),
                );

                const patientResponsibility = foundPaymentItems.reduce((acc, el) => {
                    const adjustments = _.filter(
                        el.adjustments,
                        el => el.group === PayerAdjustmentGroupType.PATIENTRESPONSIBILITY,
                    );
                    const amount = adjustments.reduce((acc2, adj) => {
                        if (el.status === PaymentStatusType.REVERSALOFPREVIOUSPAYMENT) {
                            acc2 -= this.reformatNumber(_.get(adj, 'amount.amount', '0'));
                            return acc2;
                        }

                        const isLatestPayment = latestPayments.some(payment => payment.id === el.paymentId);

                        if (!isLatestPayment) {
                            return acc2;
                        }

                        acc2 += this.reformatNumber(_.get(adj, 'amount.amount', '0'));

                        return acc2;
                    }, 0);

                    return acc + amount;
                }, 0);

                return {
                    controlNumber: line.x12ControlNumber,
                    dateOfService: this.reformatDates(line.f24aFromDateOfService, line.f24aThroughDateOfService),
                    procedureCode: line.f24dProcedureCode,
                    modifiers: line.f24dProcedureModifiers,
                    description: line.x12Narrative,
                    diagnosis: this.getDiagnosisCodes(line),
                    units: line.f24gUnits,
                    paid,
                    allowed: isFinite(allowed) ? allowed : 0,
                    accepted: this.reformatNumber(_.get(chargemasterItem, 'accepted.amount', '0')) * line.f24gUnits,
                    medicare: this.reformatNumber(_.get(chargemasterItem, 'medicare.amount', '0')),
                    patientMaxAmount: _.get(chargemasterItem, 'patient.amount')
                        ? this.reformatNumber(_.get(chargemasterItem, 'patient.amount', '0')) * line.f24gUnits
                        : undefined,
                    totalCharges: this.reformatNumber(_.get(line.f24fChargeAmount, 'amount', '0')),
                    hasExtendedX12Data: this.hasExtendedX12Data(line),
                    chargemasterItem,
                    patientResponsibility,
                    line,
                    items: foundPaymentItems,
                };
            })
            .value();

        const usedPaymentItems = professionalMatcher.getUsedPaymentItems();
        const unusedPaymentItems = paymentItems.filter(item => !usedPaymentItems.has(item));

        const notFoundByKey = new Map<string, PaymentItemWithStatus[]>();
        unusedPaymentItems.forEach(item => {
            const key = getPaymentItemKey(item, existingControlNumbers) || 'NOT_FOUND';
            const items = notFoundByKey.get(key) || [];
            items.push(item);
            notFoundByKey.set(key, items);
        });

        const notFoundRecords: IRecord[] = [];

        [...notFoundByKey.values()].forEach(items => {
            const el = _.first(items);
            if (!el) {
                return;
            }

            const paid = items.reduce((acc, el) => {
                const amount = this.reformatNumber(_.get(el, 'paidAmount.amount', '0'));
                return acc + amount;
            }, 0);

            const allowed = items.reduce((acc, el) => {
                const amount = this.reformatNumber(_.get(el, 'allowedAmount.amount', '0'));
                return acc + amount;
            }, 0);

            const totalCharges = this.reformatNumber(_.get(el, 'chargeAmount.amount', '0'));

            const adjustments = _.filter(
                items.map(item => item.adjustments || []).flat(),
                el => el.group === PayerAdjustmentGroupType.PATIENTRESPONSIBILITY,
            );
            const patientResponsibility = adjustments.reduce((acc2, adj) => {
                acc2 += this.reformatNumber(_.get(adj, 'amount.amount', '0'));

                return acc2;
            }, 0);

            const units = items.reduce((acc, el) => {
                const count = _.get(el, 'paidCount', 0);
                return acc + count;
            }, 0);

            notFoundRecords.push({
                dateOfService: this.reformatDates(el.fromDos, el.throughDos),
                procedureCode: el.procedureCode,
                modifiers: el.modifierCodes,
                ...this.getMismatchDescriptionFields(el),
                diagnosis: '',
                units,
                patientResponsibility,
                paid,
                allowed,
                medicare: undefined,
                accepted: undefined,
                totalCharges,
                hasExtendedX12Data: false,
                line: undefined,
                items,
            });
        });

        return [...parsedRecords, ...notFoundRecords];
    };

    private readonly hasExtendedX12Data = (line: SupportProfessionalTemplateServiceLine): boolean => {
        // Test for x12 fields in the service line
        const x12Fields = [
            'x12Note',
            'x12ProviderIds',
            'x12FacilityId',
            'x12DurableMedicalEquipment',
            'x12TestResults',
            'x12Paperwork',
            'x12Ambulance',
            'x12Anesthesia',
            'x12Drug',
            'x12HospiceEmployee',
            'x12Forms',
        ];
        const matched = _.pick(line, x12Fields);
        return !_.isEmpty(matched);
    };

    private readonly onRowClick = (row: Row<IRecord>) => {
        if (row.original.isSummaryRow || row.original.isMismatchGroupRow) {
            return;
        }
        (row as any).toggleRowExpanded();
    };

    private readonly renderExpanded = (row: Row<IRecord>) => {
        if (!(row as any).isExpanded) {
            return null;
        }

        const payerStatusMap: Map<number, SupportPayer | ProviderPayer> = new Map();

        _.forEach([0, 1, 2], i => {
            const subscriber = _.nth(this.props.payerSubscribers as any[], i);
            const payer = subscriber?.payer;

            if (payer) {
                payerStatusMap.set(i, payer);
            }
        });

        return (
            <S.Expand>
                {row.original.chargemasterItem && (
                    <S.DetailsTable>{this.renderDetailsData(row.original)}</S.DetailsTable>
                )}
                {!!row.original.items?.length && (
                    <S.ResourceTable>
                        <PaymentItems items={row.original.items} payerStatusMap={payerStatusMap} />
                    </S.ResourceTable>
                )}
            </S.Expand>
        );
    };

    private readonly renderDetailsData = ({ chargemasterItem, line, description }: IRecord) => {
        const {
            id,
            authorization,
            coverage,
            facility,
            paperwork,
            payer,
            provider,
            placeOfServiceType,
            procedure,
            contract,
            modifiers,
        } = chargemasterItem || {};

        let diagnosis = '';

        const template = this.props.template;
        if (template) {
            template;
            if (this.isProfessionalTemplate(template)) {
                const professionalTemplate = template as SupportProfessionalClaimTemplate;

                const pointersMap = {
                    A: professionalTemplate.f21aDiagnosisCode,
                    B: professionalTemplate.f21bDiagnosisCode,
                    C: professionalTemplate.f21cDiagnosisCode,
                    D: professionalTemplate.f21dDiagnosisCode,
                    E: professionalTemplate.f21eDiagnosisCode,
                    F: professionalTemplate.f21fDiagnosisCode,
                    G: professionalTemplate.f21gDiagnosisCode,
                    H: professionalTemplate.f21hDiagnosisCode,
                    I: professionalTemplate.f21iDiagnosisCode,
                    J: professionalTemplate.f21jDiagnosisCode,
                    K: professionalTemplate.f21kDiagnosisCode,
                    L: professionalTemplate.f21lDiagnosisCode,
                };
                diagnosis = this.getCodesByPointers(line?.f24eDiagnosisPointers || [], pointersMap).join(', ');
            } else if (this.isInstitutionalTemplate(template)) {
                // TODO: Do we even need this?
                diagnosis = '';
            } else if (this.isNynf3Template(template)) {
                // TODO: support Nynf3
                diagnosis = '';
            }
        }

        function getProviderName(provider: SupportProvider | ProviderProvider): string {
            if (provider.first || provider.last) {
                return _.chain([provider.first, provider.last]).compact().join(' ').value();
            }

            if (provider.organization) {
                return provider.organization;
            }

            return provider.id || ''; // Provider has to always have name of organization
        }

        return (
            <tbody key="details-data">
                <tr>
                    <th>Description</th>
                    <td>{description ? description : <S.EmptyLabel>Empty</S.EmptyLabel>}</td>
                </tr>
                <tr>
                    <th>Service Line Item ID</th>
                    <td>{line?.id ? line.id : <S.EmptyLabel>Empty</S.EmptyLabel>}</td>
                </tr>
                <tr>
                    <th>Chargemaster Item ID</th>
                    <td>{id ? id : <S.EmptyLabel>Empty</S.EmptyLabel>}</td>
                </tr>
                <tr>
                    <th>Facility</th>
                    <td>{facility?.name ? facility.name : <S.EmptyLabel>Empty</S.EmptyLabel>}</td>
                </tr>
                <tr>
                    <th>Payer</th>
                    <td>{payer?.name ? payer.name : <S.EmptyLabel>Empty</S.EmptyLabel>}</td>
                </tr>
                <tr>
                    <th>Provider</th>
                    <td>{provider ? getProviderName(provider) : <S.EmptyLabel>Empty</S.EmptyLabel>}</td>
                </tr>
                <tr>
                    <th>POS Type</th>
                    <td>
                        {(() => {
                            const posValue = placeOfServiceType || line?.f24bPlaceOfServiceCode;
                            if (!posValue) {
                                return <S.EmptyLabel>Empty</S.EmptyLabel>;
                            }
                            // Format enum value (e.g., "HOME" -> "Home")
                            return normalizeStr(posValue);
                        })()}
                    </td>
                </tr>
                <tr>
                    <th>Procedure</th>
                    <td>{procedure ? procedure : <S.EmptyLabel>Empty</S.EmptyLabel>}</td>
                </tr>
                <tr>
                    <th>Modifiers</th>
                    <td>{_.isEmpty(modifiers) ? <S.EmptyLabel>Empty</S.EmptyLabel> : renderStringGroup(modifiers)}</td>
                </tr>
                <tr>
                    <th>Diagnosis</th>
                    <td>{diagnosis ? diagnosis : <S.EmptyLabel>Empty</S.EmptyLabel>}</td>
                </tr>
                <tr>
                    <th>Authorization Required</th>
                    <td>
                        {_.isUndefined(authorization) ? (
                            <S.EmptyLabel>Empty</S.EmptyLabel>
                        ) : authorization ? (
                            'Yes'
                        ) : (
                            'No'
                        )}
                    </td>
                </tr>
                <tr>
                    <th>Coverage</th>
                    <td>{coverage ? coverage : <S.EmptyLabel>Empty</S.EmptyLabel>}</td>
                </tr>
                <tr>
                    <th>Paperwork Required</th>
                    <td>{_.isUndefined(paperwork) ? <S.EmptyLabel>Empty</S.EmptyLabel> : paperwork ? 'Yes' : 'No'}</td>
                </tr>
                <tr>
                    <th>Contract</th>
                    <td>
                        {contract ? (
                            renderString(contract, { normalized: true, type: 'String' })
                        ) : (
                            <S.EmptyLabel>Empty</S.EmptyLabel>
                        )}
                    </td>
                </tr>
            </tbody>
        );
    };

    private readonly reformatDates = (date1?: string, date2?: string): string | undefined => {
        if (date1 && date2) {
            return `${formatDate(date1, 'MMM d')} - ${formatDate(date2, 'MMM d, yyyy')}`;
        } else if (date1) {
            return formatDate(date1, 'MMM d, yyyy');
        }
    };

    private readonly reformatNumber = (n: string | undefined): number => {
        return n === undefined ? 0 : Number.parseFloat(n);
    };

    private readonly getMismatchDescriptionFields = (
        item: PaymentItemWithStatus,
    ): Pick<IRecord, 'description' | 'mismatchDescriptionShort' | 'isNoLikelyMatch'> => {
        const lineNumber = findLikelyMatchLineNumber(this.props.template, item);
        const mismatchDescription = buildMismatchDescription(item, lineNumber, this.reformatDates);

        return {
            description: mismatchDescription.full,
            mismatchDescriptionShort: mismatchDescription.short,
            isNoLikelyMatch: mismatchDescription.isNoLikelyMatch,
        };
    };

    private readonly getCodesByPointers = (
        pointers: string[],
        pointersMap: Record<string, string | undefined>,
    ): string[] => {
        return pointers.reduce<string[]>((acc, el) => {
            const code = pointersMap[el];
            if (code) {
                acc.push(code);
            }

            return acc;
        }, []);
    };

    private readonly getDiagnosisCodes = (line?: SupportProfessionalTemplateServiceLine): string => {
        if (!line || !this.props.template) {
            return '';
        }

        if (this.isProfessionalTemplate(this.props.template)) {
            const professionalTemplate = this.props.template as SupportProfessionalClaimTemplate;

            const pointersMap = {
                A: professionalTemplate.f21aDiagnosisCode,
                B: professionalTemplate.f21bDiagnosisCode,
                C: professionalTemplate.f21cDiagnosisCode,
                D: professionalTemplate.f21dDiagnosisCode,
                E: professionalTemplate.f21eDiagnosisCode,
                F: professionalTemplate.f21fDiagnosisCode,
                G: professionalTemplate.f21gDiagnosisCode,
                H: professionalTemplate.f21hDiagnosisCode,
                I: professionalTemplate.f21iDiagnosisCode,
                J: professionalTemplate.f21jDiagnosisCode,
                K: professionalTemplate.f21kDiagnosisCode,
                L: professionalTemplate.f21lDiagnosisCode,
            };
            return this.getCodesByPointers(line.f24eDiagnosisPointers || [], pointersMap).join(', ');
        } else if (this.isInstitutionalTemplate(this.props.template)) {
            // TODO: Implement diagnosis code extraction for institutional templates if needed
            return '';
        } else if (this.isNynf3Template(this.props.template)) {
            // TODO: Implement diagnosis code extraction for Nynf3 templates if needed
            return '';
        }

        return '';
    };
}
